import { useEffect, useState } from 'react';
import { repairAPI } from '../services/api';
import { Modal, Loading } from './ui';
import { formatRs } from '../utils/currency';

const fmtDT = (v) => v ? new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null;
const fmtD  = (v) => v ? new Date(v).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : null;

const COLORS = {
  done:    { dot:'#0FC08A', ring:'#D6F5EA', text:'#0B7A57' },
  current: { dot:'#FBB03B', ring:'#FDF0D8', text:'#8A5D08' },
  blocked: { dot:'#F0246E', ring:'#FCDDE8', text:'#A00E48' },
  todo:    { dot:'#C7CDD9', ring:'#EEF1F6', text:'#8A93A5' },
};

const Step = ({ state, title, when, who, note, last }) => {
  const c = COLORS[state] || COLORS.todo;
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ background: c.dot, boxShadow: `0 0 0 4px ${c.ring}` }} />
        {!last && <span className="flex-1 w-0.5 my-1" style={{ background: state === 'done' ? COLORS.done.dot : '#E8EDF7' }} />}
      </div>
      <div className={`pb-5 ${last ? 'pb-0' : ''}`}>
        <p className="text-sm font-semibold" style={{ color: c.text }}>{title}</p>
        {when && <p className="text-xs text-gray-400">{when}</p>}
        {who  && <p className="text-xs text-gray-500">{who}</p>}
        {note && <p className="text-xs mt-1 px-2 py-1 rounded bg-gray-50 text-gray-600 max-w-md">{note}</p>}
      </div>
    </div>
  );
};

export default function JobFlowModal({ repairId, open, onClose }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !repairId) return;
    setLoading(true); setJob(null);
    repairAPI.getOne(repairId)
      .then(r => setJob(r.data.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [open, repairId]);

  const buildSteps = (j) => {
    const approvals = Array.isArray(j.approvals) ? j.approvals : [];
    const invoices  = Array.isArray(j.invoices)  ? j.invoices  : [];
    const lastDecision = approvals[0];
    const inv = invoices.find(i => i.status !== 'rejected') || invoices[0];
    const steps = [];

    steps.push({ state:'done', title:'Repair request submitted',
      when: fmtDT(j.created_at) || fmtD(j.requested_date),
      who: j.requester?.name ? `by ${j.requester.name}` : null });

    if (['pending','revision_requested'].includes(j.status)) {
      steps.push({ state:'current', title:'Waiting for foreman review',
        note:'Locked — the officer cannot edit until a decision is made' });
    } else if (j.status === 'rejected') {
      steps.push({ state:'blocked', title:'Rejected by foreman',
        when: fmtDT(lastDecision?.action_date), who: lastDecision?.approver?.name ? `by ${lastDecision.approver.name}` : null,
        note: lastDecision?.comments || j.notes });
      steps.push({ state:'current', title:'Officer to edit and re-submit, or delete' });
    } else {
      steps.push({ state:'done', title:'Approved by foreman',
        when: fmtDT(lastDecision?.action_date) || fmtD(j.approved_date),
        who: lastDecision?.approver?.name ? `by ${lastDecision.approver.name}` : null,
        note: lastDecision?.comments });
    }

    if (!['pending','revision_requested','rejected'].includes(j.status)) {
      steps.push({ state: j.garage_id ? 'done' : 'todo',
        title: j.garage_id ? 'Job awarded to garage' : 'Award letter / garage assignment',
        note: j.quotation_no ? `Quotation ${j.quotation_no}${j.quoted_amount ? ` · ${formatRs(j.quoted_amount)}` : ''}` : null });

      if (!inv) {
        steps.push({ state:'current', title:'Waiting for garage invoice',
          note:'Officer submits the invoice once the work is done' });
      } else {
        steps.push({ state:'done', title:`Invoice ${inv.invoice_no} submitted`,
          when: fmtDT(inv.created_at), note: formatRs(inv.total_amount || inv.amount) });

        if (inv.status === 'pending_approval') {
          steps.push({ state:'current', title:'Invoice waiting for foreman approval' });
        } else if (inv.status === 'rejected') {
          steps.push({ state:'blocked', title:'Invoice rejected', note: inv.review_remark });
        } else {
          steps.push({ state:'done', title:'Invoice approved by foreman', when: fmtDT(inv.reviewed_at) });
          if (inv.status === 'pending_payment') {
            steps.push({ state:'current', title:'Waiting for Accounts payment' });
          } else if (inv.status === 'completed') {
            steps.push({ state:'done', title:'Payment recorded',
              when: fmtDT(inv.paid_at),
              note: `${inv.payment_method === 'cash' ? 'Cash' : `Cheque ${inv.cheque_no || ''}`} · ${formatRs(inv.paid_amount)}` });
          }
        }
      }
    }

    const complete = j.status === 'completed' && inv?.status === 'completed';
    steps.push({ state: complete ? 'done' : 'todo', title: complete ? 'Job complete' : 'Job completion' });
    return steps;
  };

  const steps = job ? buildSteps(job) : [];
  const currentStep = steps.find(s => s.state === 'current' || s.state === 'blocked');

  return (
    <Modal open={open} onClose={onClose} size="lg"
      title={`JOB-${String(repairId || 0).padStart(4, '0')} — status & flow`}>
      {loading ? <Loading /> : !job ? (
        <p className="text-sm text-gray-400 py-8 text-center">Could not load this job.</p>
      ) : (
        <div className="space-y-5">
          <div className="rounded-xl p-4" style={{ background: '#EAF1FE' }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold" style={{ color: '#1B3A93' }}>{job.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {job.vehicle?.reg_number} · {job.vehicle?.brand} {job.vehicle?.model} · {job.repair_type}
                </p>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-medium capitalize"
                style={{ background: '#fff', color: '#1B3A93' }}>{(job.status || '').replace('_',' ')}</span>
            </div>
            {currentStep && (
              <p className="text-xs mt-3 font-medium"
                style={{ color: currentStep.state === 'blocked' ? COLORS.blocked.text : COLORS.current.text }}>
                ⏳ Now: {currentStep.title}
              </p>
            )}
          </div>

          <div className="pl-1">
            {steps.map((s, i) => <Step key={i} {...s} last={i === steps.length - 1} />)}
          </div>
        </div>
      )}
    </Modal>
  );
}
